import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
// import { useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    IconButton,
    Grid,
    Alert,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isWithinInterval, isBefore, startOfDay } from 'date-fns';
import { useCreateTask, useGetMyDeliverables, MY_DELIVERABLES_QK } from 'services/api/task-management/trackerHooks';
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TaskWorkingPeriodPicker from 'module/task-management/components/TaskWorkingPeriodPicker';

const CreateTaskModal = ({ open, onClose, projects = [], setSuccessMessage, setErrorMessage }) => {
    // Ensure projects is always an array
    const projectsList = useMemo(() =>
        Array.isArray(projects) ? projects : [],
        [projects]
    );

    const getEffectiveProjectStatus = (project) => {
    const rawStatus = String(project?.status || '').toLowerCase().trim();

        if (rawStatus === 'completed' || rawStatus === 'complete') {
            return 'completed';
        }

        const deadline = project?.deadline ? parseISO(project.deadline) : null;
        const today = startOfDay(new Date());

        if (deadline && isBefore(startOfDay(deadline), today)) {
            return 'overdue';
        }

        return 'ongoing';
    };

    const getProjectStatusLabel = (status) => {
        if (status === 'overdue') return 'Overdue';
        if (status === 'ongoing') return 'Ongoing';
        if (status === 'completed') return 'Completed';
        return status;
    };

    const getProjectStatusPriority = (status) => {
        if (status === 'ongoing') return 0;
        if (status === 'overdue') return 1;
        return 99;
    };

    const projectOptions = useMemo(() => {
        return projectsList
            .map((project) => {
                const effectiveStatus = getEffectiveProjectStatus(project);

                return {
                    ...project,
                    effectiveStatus,
                    isDisabled: effectiveStatus === 'overdue',
                };
            })
            .filter((project) => project.effectiveStatus !== 'completed')
            .sort((a, b) => {
                const statusDiff =
                    getProjectStatusPriority(a.effectiveStatus) -
                    getProjectStatusPriority(b.effectiveStatus);

                if (statusDiff !== 0) return statusDiff;

                return String(a.name || '').localeCompare(String(b.name || ''));
            });
    }, [projectsList]);

    const [formData, setFormData] = useState({
        project_id: '',
        project_member_deliverable_id: '',
        name: '',
        value: '',
        due_date: null,
        scheduled_date: null,
        scheduled_end_date: null
    });

    const [errors, setErrors] = useState({});
    const [selectedProject, setSelectedProject] = useState(null);
    const [availableDeliverables, setAvailableDeliverables] = useState([]);
    const queryClient = useQueryClient();

    const { mutate: createTask, isPending, isError, error } = useCreateTask();
    const isSubmittingRef = React.useRef(false)
    const [showDiscardModal, setShowDiscardModal] = useState(false)

    const isDirty = useMemo(() => {
        return (
            !!formData.project_id ||
            !!formData.project_member_deliverable_id ||
            !!String(formData.name || '').trim() ||
            !!String(formData.value || '').trim() ||
            !!formData.due_date ||
            !!formData.scheduled_date ||
            !!formData.scheduled_end_date
        )
    }, [formData])

    const resetForm = () => {
        setFormData({
            project_id: '',
            project_member_deliverable_id: '',
            name: '',
            value: '',
            due_date: null,
            scheduled_date: null,
            scheduled_end_date: null
        });
        setErrors({});
        setSelectedProject(null);
        setAvailableDeliverables([]);
    }

    // Fetch my deliverables for the selected project (backend filtered for current user)
    const { data: deliverablesData, isLoading: isLoadingDeliverables } = useGetMyDeliverables(
        formData.project_id,
        { enabled: !!formData.project_id }
    );

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            resetForm()
            setShowDiscardModal(false)
        }
    }, [open]);

    // Update available deliverables when data is fetched
    useEffect(() => {
        if (deliverablesData?.data?.deliverables) {
            console.log('📦 Fetched deliverables for current user:', deliverablesData.data.deliverables);
            setAvailableDeliverables(deliverablesData.data.deliverables);
        } else {
            console.log('No deliverables returned for project', formData.project_id);
            setAvailableDeliverables([]);
        }
    }, [deliverablesData]);

    // Set selected project info when project_id changes
    useEffect(() => {
        if (formData.project_id && projects) {
            const project = projects.find(p => p.id === formData.project_id);
            setSelectedProject(project || null);
        } else {
            setSelectedProject(null);
        }
    }, [formData.project_id, projects]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error when user types
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Validate form before submission
    const validateForm = () => {
        const newErrors = {};

        if (!formData.project_id) {
            newErrors.project_id = 'Project is required';
        }

        if (!formData.project_member_deliverable_id) {
            newErrors.project_member_deliverable_id = 'Deliverable is required';
        }

        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'Task name is required';
        }

        if (!formData.value || formData.value <= 0) {
            newErrors.value = 'Value must be greater than 0';
        }

        // Validate value against remaining deliverable value WITH overflow check
        if (formData.project_member_deliverable_id && formData.value) {
            const selectedDeliverable = availableDeliverables.find(
                d => d.id === formData.project_member_deliverable_id
            );

            if (selectedDeliverable) {
                const remainingValue = parseFloat(selectedDeliverable.remaining_value);
                const enteredValue = parseFloat(formData.value);
                const overflowAllowed = selectedDeliverable.overflow_allowed;

                // Only validate if overflow is NOT allowed
                if (!overflowAllowed && enteredValue > remainingValue) {
                    newErrors.value = `Value cannot exceed ${remainingValue}. Overflow is not allowed for this deliverable.`;
                }
            }
        }

        if (!formData.due_date) {
            newErrors.due_date = 'Due date is required';
        }

        // Validate dates are within project timeline
        if (selectedProject && formData.due_date) {
            const projectStart = parseISO(selectedProject.start_date);
            const projectDeadline = parseISO(selectedProject.deadline);
            const dueDate = formData.due_date;

            if (!isWithinInterval(dueDate, { start: projectStart, end: projectDeadline })) {
                newErrors.due_date = `Due date must be between ${format(projectStart, 'MMM dd, yyyy')} and ${format(projectDeadline, 'MMM dd, yyyy')}`;
            }
        }

        if (!formData.scheduled_date) {
            newErrors.scheduled_date = 'Working period start date is required';
        }

        if (!formData.scheduled_end_date) {
            newErrors.scheduled_end_date = 'Working period end date is required';
        }

        if (formData.scheduled_date && formData.scheduled_end_date && formData.scheduled_date > formData.scheduled_end_date) {
            newErrors.scheduled_end_date = 'Working period end date must be on or after the start date';
        }

        if (formData.scheduled_end_date && formData.due_date && formData.scheduled_end_date > formData.due_date) {
            newErrors.scheduled_end_date = 'Working period end date must be on or before the due date';
        }

        if (formData.scheduled_date && formData.due_date && formData.scheduled_date > formData.due_date) {
            newErrors.scheduled_date = 'Working period start date must be on or before the due date';
        }

        // Validate working period is within project timeline
        if (selectedProject && formData.scheduled_date) {
            const projectStart = parseISO(selectedProject.start_date);
            const projectDeadline = parseISO(selectedProject.deadline);
            const scheduledDate = formData.scheduled_date;

            if (!isWithinInterval(scheduledDate, { start: projectStart, end: projectDeadline })) {
                newErrors.scheduled_date = `Working period start date must be between ${format(projectStart, 'MMM dd, yyyy')} and ${format(projectDeadline, 'MMM dd, yyyy')}`;
            }
        }

        if (selectedProject && formData.scheduled_end_date) {
            const projectStart = parseISO(selectedProject.start_date);
            const projectDeadline = parseISO(selectedProject.deadline);
            const scheduledEndDate = formData.scheduled_end_date;

            if (!isWithinInterval(scheduledEndDate, { start: projectStart, end: projectDeadline })) {
                newErrors.scheduled_end_date = `Working period end date must be between ${format(projectStart, 'MMM dd, yyyy')} and ${format(projectDeadline, 'MMM dd, yyyy')}`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true

        if (!validateForm()) {
            isSubmittingRef.current = false
            return;
        }

        // Format payload to match backend expectations
        const payload = {
            project_id: formData.project_id,
            project_member_deliverable_id: formData.project_member_deliverable_id,
            name: formData.name.trim(),
            value: parseFloat(formData.value),
            due_date: format(formData.due_date, 'yyyy-MM-dd'),
            scheduled_date: formData.scheduled_date
                ? format(formData.scheduled_date, 'yyyy-MM-dd')
                : null,
            scheduled_end_date: formData.scheduled_end_date
                ? format(formData.scheduled_end_date, 'yyyy-MM-dd')
                : null
        };

        console.log('📤 Submitting task payload:', payload);

        // Call the mutation
        createTask(payload, {
            onSuccess: async (data) => {
                isSubmittingRef.current = false
                console.log('Task created:', data);

                // Notify parent page-level banner if callback provided
                if (setSuccessMessage) {
                    setSuccessMessage('Task created successfully!')
                    setTimeout(() => setSuccessMessage(''), 5000)
                }

                queryClient.invalidateQueries([MY_DELIVERABLES_QK, formData.project_id])
                await queryClient.refetchQueries([MY_DELIVERABLES_QK, formData.project_id])

                finalClose()
            },
            onError: (error) => {
                isSubmittingRef.current = false
                console.error('❌ Create task error:', error);

                // Notify parent page-level error if callback provided
                if (setErrorMessage) {
                    const errorMsg = error?.response?.data?.message || 'Failed to create task. Please try again.'
                    setErrorMessage(errorMsg)
                    setTimeout(() => setErrorMessage(''), 5000)
                }

                // Handle validation errors from backend
                if (error.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                }
            }
        });
    };

    const finalClose = () => {
        isSubmittingRef.current = false
        setShowDiscardModal(false)
        resetForm()
        onClose();
    };

    const requestClose = (source, reason) => {
        if (isPending) return
        if (reason === 'backdropClick') return

        if (!isDirty) {
            finalClose()
            return
        }

        setShowDiscardModal(true)
    }


    return (
        <>
        <Dialog
            open={open}
            onClose={(event, reason) => requestClose('modal', reason)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">

                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                        {/* <DescriptionOutlined sx={{ color: 'primary.main' }} /> */}
                        <Box sx={{
                            width: 45,
                            height: 45,
                            borderRadius: '100%',
                            backgroundColor: '#deecff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <g clip-path="url(#clip0_1496_8684)">
                                    <path d="M8 6V4H4V22H11M8 6H16M8 6V2H16V6M16 6V4H20V11M19 15V19M19 19V23M19 19H15M19 19H23" stroke="#2666BE" stroke-width="2" stroke-linecap="square" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1496_8684">
                                        <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: 'text.primary'
                            }}
                        >
                            Create Task
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={() => requestClose('icon')}
                        sx={{ color: '#3a3a3a', ml: 1 }}
                    >
                        <CloseIcon />
                    </IconButton>

                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {/* Show API error if any */}
                {isError && error?.response?.data?.message && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error.response.data.message}
                    </Alert>
                )}

                <Box>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                        Project <span style={{ color: '#D32F2F' }}>*</span>
                    </Typography>
                    <Box display="flex" flexDirection="column" mb={2}>
                        <Autocomplete
                            options={projectOptions}
                            getOptionDisabled={(option) => option.isDisabled}
                            // options={projectsList.filter(p => p.status?.toLowerCase() !== 'completed')} // filter out completd projects from dropdown create task
                            getOptionLabel={(option) => option?.name || ''}
                            // value={projectsList.find(p => p.id === formData.project_id) || null}
                            value={projectOptions.find(p => p.id === formData.project_id) || null}
                            // onChange={(event, newValue) => {
                            //     handleChange('project_id', newValue?.id || '');
                            // }}
                            onChange={(event, newValue) => {
                                if (newValue?.isDisabled) return;

                                handleChange('project_id', newValue?.id || '');
                                handleChange('project_member_deliverable_id', '');
                                setAvailableDeliverables([]);
                            }}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search and select project"
                                    error={!!errors.project_id}
                                    helperText={errors.project_id}
                                    fullWidth
                                />
                            )}
                            noOptionsText="No projects found"
                            filterOptions={(options, state) => {
                                return options.filter((option) =>
                                    String(option?.name || '').toLowerCase().includes(state.inputValue.toLowerCase())
                                );
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, 8],
                                            },
                                        },
                                    ],
                                },
                            }}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            opacity: option.isDisabled ? 0.6 : 1,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {option.name}
                                        </Typography>

                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '999px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                backgroundColor:
                                                    option.effectiveStatus === 'overdue' ? '#FDECEC' : '#EAF7EE',
                                                color:
                                                    option.effectiveStatus === 'overdue' ? '#D32F2F' : '#2E7D32',
                                            }}
                                        >
                                            {getProjectStatusLabel(option.effectiveStatus)}
                                        </Box>
                                    </Box>
                                </li>
                            )}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Overdue projects are shown for reference and cannot be selected.
                        </Typography>
                        {errors.project_id && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {errors.project_id}
                            </Typography>
                        )}
                    </Box>

                    {/* Task Name */}
                    <Box display="flex" flexDirection="column" mb={2}>
                        <Typography variant="body2" fontWeight={500} mb={1}>
                            Task Name <span style={{ color: '#D32F2F' }}>*</span>
                        </Typography>
                        <TextField
                            placeholder="Enter task name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                        />
                    </Box>
                </Box>

                {/* Deliverables and Task Value*/}
                <Box mt={3} mb={2} gap={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={500} mb={1}>
                                Deliverable Type <span style={{ color: '#D32F2F' }}>*</span>
                            </Typography>
                            {isLoadingDeliverables ? (
                                <Box display="flex" alignItems="center" gap={1} sx={{ p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">Loading deliverables...</Typography>
                                </Box>
                            ) : (
                                <FormControl fullWidth size="small" error={!!errors.project_member_deliverable_id} disabled={!formData.project_id}>
                                    <Select
                                        value={formData.project_member_deliverable_id}
                                        onChange={(e) => handleChange('project_member_deliverable_id', e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <Typography color="text.secondary">Deliverable Type</Typography>;
                                            }
                                            const deliverable = availableDeliverables.find(d => d.id === selected);
                                            return deliverable?.deliverable_type || selected;
                                        }}
                                    >
                                        {availableDeliverables.length === 0 ? (
                                            <MenuItem disabled>
                                                <Typography variant="body2" color="text.secondary">
                                                    No deliverables available (contact Project Manager)
                                                </Typography>
                                            </MenuItem>
                                        ) : (
                                            availableDeliverables.map((deliverable) => (
                                                <MenuItem key={deliverable.id} value={deliverable.id}>
                                                    {deliverable.deliverable_type}
                                                    {' '}(Remaining: {deliverable.remaining_value})
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                    {errors.project_member_deliverable_id && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {errors.project_member_deliverable_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={500} mb={1}>
                                Deliverable Value <span style={{ color: '#D32F2F' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={formData.value}
                                onChange={(e) => handleChange('value', e.target.value)}
                                error={!!errors.value}
                                helperText={errors.value}
                                placeholder="Enter deliverable value"
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            {formData.project_member_deliverable_id && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    You have remaining {
                                        (() => {
                                            const selectedDeliverable = availableDeliverables.find(
                                                d => d.id === formData.project_member_deliverable_id
                                            );
                                            return selectedDeliverable?.remaining_value || 0;
                                        })()
                                    } credit value
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {/* Working Period */}
                <Box mt={3} mb={2}>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                        Working Period <span style={{ color: '#D32F2F' }}>*</span>
                    </Typography>
                    <TaskWorkingPeriodPicker
                        label=""
                        name="workingPeriod"
                        required
                        startDate={formData.scheduled_date}
                        endDate={formData.scheduled_end_date}
                        projectStartDate={selectedProject ? parseISO(selectedProject.start_date) : null}
                        projectDeadline={selectedProject ? parseISO(selectedProject.deadline) : null}
                        dueDate={formData.due_date}
                        disabled={isPending || !selectedProject}
                        disablePast={true}
                        startError={errors.scheduled_date}
                        endError={errors.scheduled_end_date}
                        onChange={({ startDate, endDate }) => {
                            handleChange('scheduled_date', startDate);
                            handleChange('scheduled_end_date', endDate);
                        }}
                    />
                </Box>

                {/* Due Date */}
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={500} mb={1}>
                            Due date <span style={{ color: '#D32F2F' }}>*</span>
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                value={formData.due_date}
                                onChange={(newValue) => handleChange('due_date', newValue)}
                                format="dd/MM/yyyy"
                                minDate={formData.scheduled_end_date || formData.scheduled_date || (selectedProject ? parseISO(selectedProject.start_date) : undefined)}
                                maxDate={selectedProject ? parseISO(selectedProject.deadline) : undefined}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        placeholder: 'DD/MM/YYYY',
                                        error: !!errors.due_date,
                                        helperText: errors.due_date
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>   

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} 
                    sx={{
                        borderLeft: '6px solid #F3BF3A',
                        backgroundColor: '#FDF6E2',
                        borderRadius: '4.5px',
                        p: '10px'
                    }}>
                    <TipsAndUpdatesOutlinedIcon />
                    <Typography>
                        You can reschedule your planned dates later from the Work Planner or Gantt view by updating the working period.
                    </Typography>
                </Box>

                {/* Info about status */}
                {/* <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                        • No scheduled date = Task status: <strong>Pending</strong><br />
                        • With scheduled date = Task status: <strong>Ongoing</strong>
                    </Typography>
                </Alert> */}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1, backgroundColor: '#FBF5E2' }}>
                <Button
                    onClick={() => requestClose('cancel')}
                    variant="outlined"
                    disabled={isPending}
                    sx={{
                        textTransform: 'none',
                        borderColor: '#F3BF3A',
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                        px: '16px',
                        py: '10px',
                        minWidth: '150px',
                        borderRadius: '100px',
                        '&:hover': {
                            borderColor: '#f3bf3a',
                            backgroundColor: '#FDF6E2'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        isPending ||
                        !formData.project_id ||
                        !formData.project_member_deliverable_id ||
                        !formData.name ||
                        !formData.value ||
                        !formData.due_date ||
                        !formData.scheduled_date ||
                        !formData.scheduled_end_date
                    }
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#F3BF3A',
                        px: '14px',
                        py: '12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        boxShadow: 'none',
                        borderRadius: '100px',
                        '&:hover': {
                            backgroundColor: '#D09A01',
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isPending ? (
                        <>
                            <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                            Creating...
                        </>
                    ) : (
                        'Add task'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
        <AdxInfoModal
            variant="error"
            open={showDiscardModal}
            onClose={() => setShowDiscardModal(false)}
            muiIcon="warning"
            iconColor="error.main"
            title="Discard task draft?"
            subtitle="Any unsaved changes will be discarded"
            footerButtons
            firstBtnLabel="Discard"
            firstBtnHandle={finalClose}
            secondBtnLabel="Continue editing"
            secondBtnHandle={() => setShowDiscardModal(false)}
        />
        </>
    )
}

export default CreateTaskModal;
